import { CdkDrag } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  EligibleAllocation,
  StrictAllocations,
  SubAccount,
  Trade
} from '@approot/models';
import {
  AlertsService,
  ConfigService,
  RfqService,
  TradesCaptureService
} from '@approot/services';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';

export type State = 'Cancelling';

const WIN_POSITION_MIN_X = 8;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;

@Component({
  selector: 'app-trade-manager',
  templateUrl: './trade-manager.component.html',
  styleUrls: ['./trade-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeManagerComponent implements OnInit, OnChanges {
  @Input() public isModalOpened = true;
  @Input() public selectedTrades: Trade[];
  @Output() public modalStateChanged = new EventEmitter<boolean>();
  @ViewChild(CdkDrag) public dragEl: CdkDrag;
  @ViewChild('cancelDealDlg')
  private cancelDealDlg!: ElementRef<HTMLDialogElement>;
  @ViewChild('amendTradeDlg')
  private amendTradeDlg!: ElementRef<HTMLDialogElement>;

  public allTradesArchived = false;
  public allowCancelOrAmend = false;
  public currentDealReference: string;
  public currentTradeId: string;
  public dragging = false;
  public eligibleAllocations: EligibleAllocation[];
  public fullTradeForAllocation: Trade = null;
  public ineligibleReason: string = null;
  public loadingSubAccounts = false;
  public showAllocations = false;
  public showArchiveTrades = false;
  public showLoadTradesVersions = false;
  public showMenuButtons = true;
  public state: State = null;
  public strictAllocations: StrictAllocations;
  public subAccounts: SubAccount[] = [];
  public tradeAllocated = false;
  public windowLeft = 50;
  public windowTop = 50;

  private alertService = inject(AlertsService);
  private changeRef = inject(ChangeDetectorRef);
  private configService = inject(ConfigService);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private rfqService = inject(RfqService);
  private tradesCaptureService = inject(TradesCaptureService);
  private winIdx = inject(WindowIndexService);

  public ngOnInit(): void {
    this.updateZIndex();
    this.windowTop = 50 + Math.random() * (window.innerHeight / 8);
    this.windowLeft =
      window.innerWidth / 4 + Math.random() * (window.innerWidth / 4);
    this.getSubAccounts();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const parent = this.configService.userSettings().parent;
    this.ineligibleReason = null;
    if (
      changes.selectedTrades.currentValue &&
      changes.selectedTrades.currentValue.length > 0
    ) {
      if (
        parent.enableAllocations &&
        this.eligibleAllocations &&
        this.selectedTrades[0].productType !== 'vanillaoption'
      ) {
        this.ineligibleReason = 'Only Vanilla options can be allocated';
      }
    }
    if (this.selectedTrades.length === 1) {
      this.allowCancelOrAmend = parent.allowCancelOrAmend;
    } else {
      this.allowCancelOrAmend = false;
    }
  }

  public returnToMenu() {
    this.showMenuButtons = true;
    this.showLoadTradesVersions = false;
    this.showArchiveTrades = false;
    this.showAllocations = false;
  }

  public modalChanged(event: boolean) {
    this.modalStateChanged.emit(event);
  }

  public dragStart() {
    this.dragging = true;
  }

  public async dragEnd() {
    this.dragging = false;
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.trade-manager-drag'
    );
    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - rect.x;
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public updateZIndex() {
    this.winIdx.setWindowIndex(
      this.elementRef.nativeElement.querySelector('.trade-manager-drag'),
      this.renderer
    );
  }

  public close() {
    this.fullTradeForAllocation = null;
    this.modalStateChanged.emit(false);
  }

  public openTradeAllocations() {
    if (this.selectedTrades.length > 1) {
      return;
    }
    this.tradesCaptureService
      .getTradesByTradeIds(this.selectedTrades.map(t => t.tradeId))
      .subscribe(t => {
        this.fullTradeForAllocation = t[0];
        this.showAllocations = true;
        this.showMenuButtons = false;
        this.changeRef.markForCheck();
      });
  }
  public openCancelDeal() {
    this.currentDealReference = this.selectedTrades[0].dealReference;
    this.cancelDealDlg.nativeElement.showModal();
  }

  public confirmCancelDeal(dealReference: string) {
    const payload = {
      task: 'cancelDeal',
      dealReference
    };
    this.state = 'Cancelling';
    this.rfqService.tradeService(payload).subscribe({
      next: () => {
        this.amendTradeDlg.nativeElement.close();
        this.state = null;
        this.changeRef.markForCheck();
      },
      error: () => {
        this.alertService.addAlert({
          message: 'Failed to cancel deal',
          type: 'error'
        });
        this.state = null;
        this.changeRef.markForCheck();
      }
    });
    this.cancelDealDlg.nativeElement.close();
  }

  public openAmendTrade() {
    this.currentTradeId = this.selectedTrades[0].tradeId;
    this.amendTradeDlg.nativeElement.showModal();
  }

  public confirmAmendTrade(tradeId: string) {
    const payload = {
      task: 'amendTrade',
      tradeId
    };
    this.rfqService.tradeService(payload).subscribe({
      next: () => {
        this.amendTradeDlg.nativeElement.close();
      },
      error: () => {
        this.alertService.addAlert({
          message: 'Failed to amend trade',
          type: 'error'
        });
      }
    });
  }

  private getSubAccounts(): void {
    if (this.ineligibleReason != null) {
      return;
    }
    this.tradeAllocated = this.selectedTrades[0]?.isAllocated;
    const allocationEnabled =
      !!this.tradesCaptureService.dvEntities?.enableAllocations;
    if (
      allocationEnabled &&
      this.selectedTrades.length &&
      !this.tradeAllocated
    ) {
      this.loadingSubAccounts = true;
      this.tradesCaptureService
        .allocationsAvailableSubAccounts({
          entity: this.selectedTrades[0].counterPartyA
        })
        .subscribe({
          next: val => {
            this.strictAllocations = val.response.strictAllocations;
            this.eligibleAllocations = val.response.eligibleAllocationKeys.map(
              key => {
                return {
                  key,
                  values: val.response.subAccounts
                    .map(account => account[key])
                    .filter(y => y)
                };
              }
            );
            this.subAccounts = val.response.subAccounts;
            this.loadingSubAccounts = false;
            this.changeRef.markForCheck();
          },
          error: () => {
            this.loadingSubAccounts = false;
          }
        });
    }
  }
}
